/**
 * Diagnostic Intelligence System Types
 * 
 * Types for diagnostic competency scoring, mismatch detection,
 * and training recommendations.
 */

/**
 * Sample status based on diagnostic count thresholds
 */
export type DiagnosticSampleStatus = 'learning' | 'established' | 'expert';

/**
 * Mismatch rule severity levels
 */
export type MismatchSeverity = 'info' | 'warning' | 'concern';

/**
 * Mismatch rule keys
 */
export type MismatchRuleKey = 
  | 'overconfidence'
  | 'underconfidence'
  | 'slow_miss'
  | 'fast_correct'
  | 'false_negative'
  | 'repeated_unconfirmed';

/**
 * Training recommendation types
 */
export type RecommendationType = 
  | 'skill_gap'
  | 'confidence_calibration'
  | 'efficiency'
  | 'pattern_correction';

/**
 * Recommendation status
 */
export type RecommendationStatus = 'active' | 'dismissed' | 'completed' | 'expired';

/**
 * Technician diagnostic stats from database
 */
export interface TechnicianDiagnosticStats {
  id: string;
  technician_profile_id: string;
  category_key: string | null;
  total_diagnostics: number;
  avg_confidence: number | null;
  outcome_success_rate: number | null;
  calibration_score: number | null;
  complaint_validation_rate: number | null;
  avg_diagnostic_minutes: number | null;
  diagnostic_competency_score: number | null;
  sample_status: DiagnosticSampleStatus;
  last_updated_at: string;
  created_at: string;
  /**
   * Oct 20 addendum S2 — scoring version 2 breakdown. Absent/null on a row
   * produced before S2 (version 1). See src/lib/passport/competencyAnalytics.ts.
   */
  scored_sample_size?: number | null;
  scoring_version?: number | null;
  scoring_policy?: Record<string, unknown> | null;
  evidence_window_days?: number | null;
  dedicated_diagnostic_jobs?: number | null;
  inspection_jobs?: number | null;
  unclassified_jobs?: number | null;
  dedicated_diagnostic_scored?: number | null;
  inspection_scored?: number | null;
  diagnostic_quality?: number | null;
  inspection_quality?: number | null;
  diagnostic_units?: number | null;
  inspection_units_uncapped?: number | null;
  inspection_unit_share_uncapped?: number | null;
  inspection_share_uncapped?: number | null;
  inspection_units_credited?: number | null;
  inspection_share_credited?: number | null;
  cap_reason?: string | null;
  score_diagnostic_points?: number | null;
  score_inspection_points?: number | null;
  score_neutral_points?: number | null;
  classification_provenance?: Record<string, number> | null;
  previous_scoring_version?: number | null;
  previous_competency_score?: number | null;
  last_recalculation_reason?: string | null;
  last_recalculation_kind?: string | null;
  scoring_recalculated_at?: string | null;
}

/**
 * Diagnostic mismatch event from database
 */
export interface DiagnosticMismatchEvent {
  id: string;
  technician_profile_id: string;
  pensieve_event_id: string | null;
  rule_key: MismatchRuleKey;
  severity: MismatchSeverity;
  confidence_given: number | null;
  outcome: string | null;
  diagnostic_minutes: number | null;
  expected_outcome: string | null;
  complaint_confirmed: boolean | null;
  explanation: string;
  category_key: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

/**
 * Diagnostic training recommendation from database
 */
export interface DiagnosticTrainingRecommendation {
  id: string;
  technician_profile_id: string;
  category_key: string;
  recommendation_type: RecommendationType;
  priority: number;
  source_rule_key: string | null;
  source_mismatch_event_id: string | null;
  suggested_artifact_tags: string[];
  explanation: string | null;
  status: RecommendationStatus;
  dismissed_at: string | null;
  dismissed_by: string | null;
  completed_at: string | null;
  created_at: string;
  expires_at: string | null;
}

/**
 * Input for DCS calculation
 */
export interface DCSCalculationInput {
  avgConfidence: number; // 1-5 scale
  outcomeSuccessRate: number; // 0-100
  complaintValidationRate: number; // 0-100
  avgDiagnosticMinutes: number;
  expectedAvgMinutes: number; // For efficiency calculation
  sampleCount: number;
}

/**
 * DCS calculation result
 */
export interface DCSResult {
  diagnosticCompetencyScore: number; // 0-100
  calibrationScore: number; // 0-100
  sampleStatus: DiagnosticSampleStatus;
  breakdown: {
    calibrationComponent: number;
    outcomeComponent: number;
    complaintComponent: number;
    efficiencyComponent: number;
  };
}

/**
 * Mismatch rule definition
 */
export interface MismatchRule {
  key: MismatchRuleKey;
  name: string;
  severity: MismatchSeverity;
  evaluate: (context: MismatchRuleContext) => boolean;
  getExplanation: (context: MismatchRuleContext) => string;
  trainingRecommendation?: {
    type: RecommendationType;
    priority: number;
    tags: string[];
  };
}

/**
 * Context for mismatch rule evaluation
 */
export interface MismatchRuleContext {
  confidence: number; // 1-5
  outcomeCorrect: boolean;
  outcome: string;
  complaintConfirmed: boolean;
  diagnosticMinutes: number;
  expectedMinutes: number;
  categoryKey: string;
  // For trend-based rules
  recentStats?: {
    complaintValidationRate: number;
    sampleCount: number;
  };
}

/**
 * Diagnostic quality bonus for Smart Assign
 */
export interface DiagnosticQualityBonus {
  bonus: number; // Always >= 0 (Tweak #3)
  dcs: number;
  sampleStatus: DiagnosticSampleStatus;
  explanation: string;
}

/**
 * Sample status thresholds
 */
export const SAMPLE_STATUS_THRESHOLDS = {
  learning: 10, // < 10 diagnostics
  established: 50, // 10-49 diagnostics
  expert: 50, // >= 50 diagnostics
} as const;

/**
 * DCS weight configuration
 */
export const DCS_WEIGHTS = {
  calibration: 0.35, // Confidence vs outcome alignment
  outcomeSuccess: 0.30, // % of correct outcomes
  complaintValidation: 0.20, // Complaint confirmed accuracy
  efficiency: 0.15, // Diagnostic time efficiency
} as const;

/**
 * Smart Assign diagnostic weight constants (Tweak #3)
 */
export const DIAGNOSTIC_SMART_ASSIGN = {
  WEIGHT_MIN: 0, // NEVER negative
  WEIGHT_MAX: 0.05, // 5% max influence
} as const;
